/**
 * Read more / read less for long rich text
 * --------------------------------------------------------------------------
 * Paired with snippets/read-more-text.liquid.
 *
 * Two jobs:
 *   1. Only show the toggle when the text genuinely exceeds the line cap. How
 *      many lines a paragraph takes depends on the viewport, the font and
 *      whether webfonts have loaded, so this is measured rather than guessed,
 *      and re-measured when any of that changes.
 *   2. Expand and collapse, keeping aria-expanded and the label in step.
 *
 * The clamp itself lives in CSS, so the text is never briefly full-height
 * before collapsing.
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('read-more-text')) return;

  // Sub-pixel line heights mean scrollHeight can exceed clientHeight by a
  // fraction on text that fits perfectly. Anything under a couple of pixels is
  // rounding, not overflow.
  var OVERFLOW_TOLERANCE = 2;

  class ReadMoreText extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-read-more-content]');
      this.toggle = this.querySelector('[data-read-more-toggle]');
      if (!this.content || !this.toggle) return;

      this.moreText = this.dataset.moreText || 'Read more';
      this.lessText = this.dataset.lessText || 'Read less';

      this.onToggleClick = this.onToggleClick.bind(this);
      this.measure = this.measure.bind(this);

      this.toggle.addEventListener('click', this.onToggleClick);
      this.measure();

      // A width change alters how many lines the text occupies.
      if ('ResizeObserver' in window) {
        var self = this;
        var frame = null;
        this.observer = new ResizeObserver(function () {
          if (frame) cancelAnimationFrame(frame);
          frame = requestAnimationFrame(self.measure);
        });
        this.observer.observe(this.content);
      } else {
        window.addEventListener('resize', this.measure);
      }

      // Webfonts usually land after first paint and change the line count.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.measure).catch(function () {});
      }
    }

    disconnectedCallback() {
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggleClick);
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('resize', this.measure);
    }

    get expanded() {
      return this.hasAttribute('data-expanded');
    }

    /**
     * Decide whether the toggle is needed at all. Skipped while expanded, since
     * an expanded element never overflows and the answer would always be "no".
     */
    measure() {
      if (this.expanded) return;

      var overflows =
        this.content.scrollHeight - this.content.clientHeight > OVERFLOW_TOLERANCE;

      this.toggle.hidden = !overflows;
      this.toggleAttribute('data-overflowing', overflows);
    }

    onToggleClick() {
      var willExpand = !this.expanded;

      this.toggleAttribute('data-expanded', willExpand);
      this.toggle.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
      this.toggle.textContent = willExpand ? this.lessText : this.moreText;

      if (willExpand) return;

      // On collapse, bring the top of the text back into view — otherwise the
      // page jumps and the reader loses their place.
      var top = this.getBoundingClientRect().top;
      if (top < 0) {
        this.scrollIntoView({
          block: 'start',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth'
        });
      }

      this.measure();
    }
  }

  customElements.define('read-more-text', ReadMoreText);
})();
